import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BookingHome,
  BookingReward,
  fetchBookingHome,
  holdBookingReward,
  portalDisplayName,
  portalLogout,
  portalPasswordLogin,
  portalPasswordReset,
  portalSignup,
  readPortalSession,
  redeemBookingWallet,
  releaseBookingReward,
  requestPortalCode,
  saveBookingSession,
  scorePassword,
  subscribePortalSession,
} from '../../lib/portal';

type AuthMode = 'login' | 'signup' | 'reset';

export function BookingAccountButton({
  slug,
  prefillEmail,
  onCustomer,
  onOpenReady,
}: {
  slug: string;
  prefillEmail?: string;
  onCustomer: (home: BookingHome | null) => void;
  onOpenReady?: (open: (mode?: AuthMode) => void) => void;
}) {
  const onCustomerRef = useRef(onCustomer);
  onCustomerRef.current = onCustomer;
  const [signedIn, setSignedIn] = useState(() => !!readPortalSession(slug, 'booking'));
  const [open, setOpen] = useState<'account' | AuthMode | null>(null);
  const [home, setHome] = useState<BookingHome | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  const load = useCallback(async () => {
    const session = readPortalSession(slug, 'booking');
    setSignedIn(!!session);
    if (!session) {
      setHome(null);
      setName('');
      onCustomerRef.current(null);
      return;
    }
    setName(session.displayName || '');
    try {
      const next = await fetchBookingHome(slug);
      if (!next?.profile) throw new Error('Could not load account');
      setHome(next);
      setName(portalDisplayName(next.profile));
      onCustomerRef.current(next);
    } catch {
      setHome(null);
      onCustomerRef.current(null);
    }
  }, [slug]);

  useEffect(() => {
    void load();
    return subscribePortalSession(slug, () => { void load(); }, 'booking');
  }, [slug, load]);

  useEffect(() => {
    onOpenReady?.((mode) => {
      setError('');
      setEmail(prefillEmail || '');
      setOpen(mode || 'login');
    });
  }, [onOpenReady, prefillEmail]);

  const initials = (name || 'Account')
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  async function signIn(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await portalPasswordLogin(slug, email.trim(), password, 'booking');
      saveBookingSession(slug, result);
      setOpen(null);
      setPassword('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in');
    } finally {
      setBusy(false);
    }
  }

  async function sendCode(mode: 'signup' | 'reset') {
    setBusy(true);
    setError('');
    try {
      const res = await requestPortalCode(slug, email.trim(), mode, 'booking');
      setCodeSent(!res.skipCode);
      if (res.skipCode) setCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the code');
    } finally {
      setBusy(false);
    }
  }

  async function createAccount(event: React.FormEvent) {
    event.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = codeSent
        ? await portalSignup(slug, email.trim(), code.trim(), password, firstName.trim(), lastName.trim(), 'booking')
        : await portalSignup(slug, email.trim(), '', password, firstName.trim(), lastName.trim(), 'booking');
      saveBookingSession(slug, result);
      setOpen(null);
      setPassword('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the account');
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await portalPasswordReset(slug, email.trim(), code.trim(), password, 'booking');
      saveBookingSession(slug, result);
      setOpen(null);
      setPassword('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset the password');
    } finally {
      setBusy(false);
    }
  }

  async function redeem(reward: BookingReward) {
    setBusy(true);
    setError('');
    try {
      if (reward.kind === 'wallet_credit') {
        await redeemBookingWallet(slug, reward.id);
      } else {
        await holdBookingReward(slug, reward.id);
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not redeem that reward');
    } finally {
      setBusy(false);
    }
  }

  async function signOut() {
    await portalLogout(slug, 'booking');
    setOpen(null);
    setHome(null);
    onCustomer(null);
  }

  const strength = scorePassword(password);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setError('');
          setOpen(signedIn ? 'account' : 'login');
        }}
        className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/20 hover:bg-white/30 text-white text-sm font-semibold"
        aria-label={signedIn ? 'Your account' : 'Sign in'}
      >
        {signedIn && initials ? initials : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        )}
      </button>

      {open === 'account' && home && createPortal(
        <div className="fixed inset-0 z-[80] bg-black/40" onClick={() => setOpen(null)}>
          <div
            className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <p className="text-lg font-semibold text-gray-900">{portalDisplayName(home.profile)}</p>
                <p className="text-sm text-gray-500">{home.profile.email}</p>
              </div>
              <button type="button" onClick={() => setOpen(null)} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-5 space-y-6">
              {error && <p className="text-sm text-red-600">{error}</p>}
              <section className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">Points</p>
                  <p className="text-xl font-semibold text-gray-900">{home.points.toLocaleString()}</p>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">Wallet</p>
                  <p className="text-xl font-semibold text-gray-900">${Number(home.profile.wallet || 0).toFixed(2)}</p>
                </div>
              </section>
              <section>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Rewards</h3>
                {home.rewards.length === 0 ? (
                  <p className="text-sm text-gray-500">No rewards to redeem yet.</p>
                ) : (
                  <div className="space-y-2">
                    {home.rewards.map((reward) => {
                      const affordable = home.points >= reward.points_cost;
                      const waiting = home.held?.redemption_id === reward.id;
                      const detail = reward.kind === 'percent_discount'
                        ? `${Number(reward.percent_off)}% off your next booking`
                        : reward.kind === 'wallet_credit'
                          ? `$${(Number(reward.value_cents || 0) / 100).toFixed(2)} wallet credit`
                          : `$${(Number(reward.value_cents || 0) / 100).toFixed(2)} off your next booking`;
                      return (
                        <div key={reward.id} className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 p-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{reward.name}</p>
                            <p className="text-xs text-gray-500">{reward.points_cost.toLocaleString()} points · {detail}</p>
                          </div>
                          {waiting ? (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => { void releaseBookingReward(slug).then(load); }}
                              className="text-xs font-semibold text-gray-600"
                            >
                              Waiting
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={busy || !affordable}
                              onClick={() => { void redeem(reward); }}
                              className="text-xs font-semibold text-blue-700 disabled:text-gray-300"
                            >
                              Redeem
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
              <History title="Bookings" empty="No bookings yet." rows={home.bookings.map((booking) => ({
                id: booking.id,
                title: booking.title || `Booking ${booking.booking_number ?? ''}`.trim(),
                meta: [booking.start_date, booking.start_time].filter(Boolean).join(' · '),
                amount: booking.total_amount != null ? `$${Number(booking.total_amount).toFixed(2)}` : '',
              }))} />
              <History title="Invoices" empty="No invoices yet." rows={home.invoices.map((invoice) => ({
                id: invoice.id,
                title: invoice.invoice_number || 'Invoice',
                meta: invoice.status,
                amount: `$${(Number(invoice.total_cents || 0) / 100).toFixed(2)}`,
              }))} />
              <button type="button" onClick={() => { void signOut(); }} className="text-sm font-medium text-gray-500 hover:text-gray-800">
                Sign out
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {open && open !== 'account' && createPortal(
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => setOpen(null)}>
          <form
            onSubmit={open === 'login' ? signIn : open === 'reset' ? resetPassword : createAccount}
            onClick={(event) => event.stopPropagation()}
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-5 space-y-3"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {open === 'login' ? 'Sign in' : open === 'reset' ? 'Reset password' : 'Create account'}
              </h2>
              <button type="button" onClick={() => setOpen(null)} className="text-gray-400" aria-label="Close">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <p className="text-sm text-gray-500">Use the email already on file if you are an existing customer.</p>
            {error && <p className="text-sm text-red-600">{error}</p>}
            {open === 'signup' && (
              <div className="grid grid-cols-2 gap-3">
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm" />
                <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" className="rounded-xl border border-gray-300 px-3 py-2.5 text-sm" />
              </div>
            )}
            <input type="email" required value={email} onChange={(e) => { setEmail(e.target.value); setCodeSent(false); }} placeholder="Email" className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm" />
            {open !== 'login' && (
              <div className="flex gap-2">
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Email code" className="flex-1 rounded-xl border border-gray-300 px-3 py-2.5 text-sm" />
                <button type="button" disabled={busy || !email.trim()} onClick={() => { void sendCode(open === 'reset' ? 'reset' : 'signup'); }} className="px-3 rounded-xl bg-gray-100 text-sm font-medium text-gray-800">
                  {codeSent ? 'Resend' : 'Send code'}
                </button>
              </div>
            )}
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" autoComplete={open === 'login' ? 'current-password' : 'new-password'} className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm" />
            {open !== 'login' && password.length > 0 && (
              <p className="text-xs" style={{ color: strength.color }}>{strength.label}</p>
            )}
            <button type="submit" disabled={busy} className="w-full py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:bg-gray-200">
              {busy ? 'Please wait…' : open === 'login' ? 'Sign in' : open === 'reset' ? 'Save password' : 'Create account'}
            </button>
            <div className="flex justify-between text-sm">
              {open === 'login' ? (
                <>
                  <button type="button" className="text-blue-700 font-medium" onClick={() => { setOpen('signup'); setError(''); setCodeSent(false); }}>Create account</button>
                  <button type="button" className="text-gray-500" onClick={() => { setOpen('reset'); setError(''); setCodeSent(false); }}>Forgot password</button>
                </>
              ) : (
                <button type="button" className="text-blue-700 font-medium" onClick={() => { setOpen('login'); setError(''); }}>Sign in</button>
              )}
            </div>
          </form>
        </div>,
        document.body,
      )}
    </>
  );
}

function History({ title, empty, rows }: { title: string; empty: string; rows: Array<{ id: string; title: string; meta: string; amount: string }> }) {
  return (
    <section>
      <h3 className="text-sm font-semibold text-gray-900 mb-2">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">{empty}</p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.id} className="flex items-center justify-between gap-3 text-sm">
              <div className="min-w-0">
                <p className="font-medium text-gray-900 truncate">{row.title}</p>
                <p className="text-xs text-gray-500">{row.meta}</p>
              </div>
              <p className="text-gray-700 shrink-0">{row.amount}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
